import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack } from '@mui/joy';

export interface ImageCanvasViewerProps {
  imageUrl: string;
  /**
   * Fallback image URLs to try if `imageUrl` fails to load (404, etc).
   * These are attempted in order.
   */
  fallbackUrls?: string[];
  overallWidthMm: number | null;
  overallHeightMm: number | null;
  onExportPng?: (dataUrl: string) => void;
  heightPx?: number;
  /** Padding around the image inside the canvas (smaller = image appears larger). */
  marginPx?: number;
  /**
   * When true (default), use crossOrigin='anonymous' for canvas export support.
   * When false, omit it so external URLs (e.g. Supabase storage) load without CORS.
   * Use false for display-only thumbnails when images fail to load.
   */
  useCrossOrigin?: boolean;
}

const DEFAULT_MARGIN = 36;
const LABEL_FONT = '13px sans-serif';

function fitAspect(srcW: number, srcH: number, dstW: number, dstH: number) {
  const srcRatio = srcW / srcH;
  const dstRatio = dstW / dstH;
  if (srcRatio > dstRatio) {
    const w = dstW; const h = w / srcRatio;
    return { x: 0, y: (dstH - h) / 2, w, h } as const;
  } else {
    const h = dstH; const w = h * srcRatio;
    return { x: (dstW - w) / 2, y: 0, w, h } as const;
  }
}

export const ImageCanvasViewer: React.FC<ImageCanvasViewerProps> = ({
  imageUrl,
  fallbackUrls,
  overallWidthMm,
  overallHeightMm,
  onExportPng,
  heightPx = 320,
  marginPx = DEFAULT_MARGIN,
  useCrossOrigin = true,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null);
  const [cssSize, setCssSize] = useState<{ w: number; h: number }>({ w: 0, h: heightPx });

  useEffect(() => {
    const urlsRaw = [imageUrl, ...(fallbackUrls || [])].filter(Boolean).map(String);
    const urls: string[] = [];
    for (const u of urlsRaw) {
      if (!urls.includes(u)) urls.push(u);
    }

    let cancelled = false;
    const img = new Image();
    if (useCrossOrigin) {
      img.crossOrigin = 'anonymous';
    }

    const tryLoad = (idx: number) => {
      if (cancelled) return;
      const next = urls[idx];
      if (!next) {
        setImgEl(null);
        return;
      }
      img.onload = () => {
        if (cancelled) return;
        setImgEl(img);
      };
      img.onerror = () => {
        if (cancelled) return;
        tryLoad(idx + 1);
      };
      // For full URLs, use as-is to avoid breaking Supabase storage URLs.
      // For paths, encode spaces and other unsafe characters.
      img.src = next.startsWith('http://') || next.startsWith('https://') ? next : encodeURI(next);
    };

    tryLoad(0);
    return () => {
      cancelled = true;
      setImgEl(null);
    };
  }, [imageUrl, fallbackUrls, useCrossOrigin]);

  useEffect(() => {
    const el = containerRef.current; if (!el) return;
    const obs = new ResizeObserver(entries => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setCssSize({ w: cr.width, h: heightPx });
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [heightPx]);

  const redrawKey = useMemo(
    () => `${imageUrl}|${overallWidthMm}|${overallHeightMm}|${cssSize.w}x${cssSize.h}|m=${marginPx}`,
    [imageUrl, overallWidthMm, overallHeightMm, cssSize.w, cssSize.h, marginPx]
  );

  useEffect(() => {
    const handle = setTimeout(() => {
      const canvas = canvasRef.current; if (!canvas) return;
      const ctx = canvas.getContext('2d'); if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const cssW = Math.max(1, Math.floor(cssSize.w));
      const cssH = Math.max(1, Math.floor(cssSize.h));
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      canvas.width = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx.clearRect(0, 0, cssW, cssH);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, cssW, cssH);

      const m = Math.max(0, Math.floor(marginPx));
      const innerX = m;
      const innerY = m;
      const innerW = Math.max(1, cssW - m * 2);
      const innerH = Math.max(1, cssH - m * 2);

      if (imgEl && imgEl.width > 0 && imgEl.height > 0) {
        const r = fitAspect(imgEl.width, imgEl.height, innerW, innerH);
        const imgRect = { x: innerX + r.x, y: innerY + r.y, w: r.w, h: r.h };
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(imgEl, imgRect.x, imgRect.y, imgRect.w, imgRect.h);


      }
    }, 80);
    return () => clearTimeout(handle);
  }, [redrawKey, imgEl]);

  const handleExport = () => {
    if (!onExportPng) return;
    const canvas = canvasRef.current; if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    onExportPng(dataUrl);
  };

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <Box ref={containerRef} sx={{ width: '100%', height: `${heightPx}px`, position: 'relative', backgroundColor: '#f8fafc', borderRadius: 'sm', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </Box>
      {onExportPng && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outlined" onClick={handleExport}>Export PNG</Button>
        </Box>
      )}
    </Stack>
  );
};

export default ImageCanvasViewer;


